import { useQuery } from "@tanstack/react-query";
import {
  BellRing,
  ChevronLeft,
  ChevronRight,
  Megaphone,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { CarouselItem } from "../api/engagement";
import { engagementApi } from "../api/engagement";
import { useCopy } from "../i18n/copy";
import { htmlToText, sanitizeHtml } from "../utils/sanitizeHtml";

function externalBannerUrl(item: CarouselItem) {
  if (String(item.type) !== "2" || !item.jump_url) return "";
  try {
    const url = new URL(item.jump_url, window.location.origin);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.href
      : "";
  } catch {
    return "";
  }
}

export function HomeCarousel({ items }: { items: CarouselItem[] }) {
  const { tr } = useCopy();
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (active >= items.length) setActive(0);
  }, [active, items.length]);

  useEffect(() => {
    if (items.length < 2) return;
    const timer = window.setInterval(
      () => setActive((current) => (current + 1) % items.length),
      5000,
    );
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (!items.length) return null;

  const move = (direction: number) =>
    setActive((current) =>
      (current + direction + items.length) % items.length,
    );

  return (
    <section
      className="home-carousel"
      aria-roledescription={tr("Carrusel")}
      aria-label={tr("Novedades")}
    >
      <div className="home-carousel-track">
        {items.map((item, index) => {
          const image = item.url_full || item.url;
          const target = externalBannerUrl(item);
          const slide = (
            <img
              src={image}
              alt=""
              loading={index === 0 ? "eager" : "lazy"}
              draggable="false"
            />
          );
          return (
            <div
              className={`home-carousel-slide${index === active ? " active" : ""}`}
              aria-hidden={index !== active}
              key={`${image}-${index}`}
            >
              {target ? (
                <a href={target} target="_blank" rel="noreferrer">
                  {slide}
                </a>
              ) : (
                slide
              )}
            </div>
          );
        })}
      </div>
      {items.length > 1 && (
        <>
          <button
            className="carousel-arrow previous"
            type="button"
            onClick={() => move(-1)}
            aria-label={tr("Anterior")}
          >
            <ChevronLeft />
          </button>
          <button
            className="carousel-arrow next"
            type="button"
            onClick={() => move(1)}
            aria-label={tr("Siguiente")}
          >
            <ChevronRight />
          </button>
          <div className="carousel-dots">
            {items.map((item, index) => (
              <button
                type="button"
                className={index === active ? "active" : ""}
                onClick={() => setActive(index)}
                aria-label={`${tr("Ir a la imagen")} ${index + 1}`}
                aria-current={index === active ? "true" : undefined}
                key={`${item.url_full || item.url}-dot`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

export function HomeNoticeTicker({ content }: { content: string }) {
  const text = useMemo(() => htmlToText(content).trim(), [content]);
  if (!text) return null;

  return (
    <section className="home-notice-ticker" aria-label={text}>
      <span className="ticker-icon" aria-hidden="true">
        <Megaphone />
      </span>
      <div className="ticker-window" aria-hidden="true">
        <div className="ticker-motion">
          <span>{text}</span>
          <span>{text}</span>
        </div>
      </div>
    </section>
  );
}

export function HomeAnnouncementDialog({ content }: { content: string }) {
  const { tr } = useCopy();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (content.trim()) setOpen(true);
  }, [content]);

  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open]);

  if (!open || !content.trim()) return null;

  return (
    <div
      className="action-sheet-backdrop home-announcement-backdrop"
      role="presentation"
      onClick={() => setOpen(false)}
    >
      <section
        className="action-sheet remote-notice-dialog home-announcement-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="home-announcement-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <span>
            <BellRing />
          </span>
          <button
            className="icon-action"
            onClick={() => setOpen(false)}
            aria-label={tr("Cerrar")}
          >
            <X />
          </button>
        </header>
        <h2 id="home-announcement-title">{tr("Aviso")}</h2>
        <div
          className="remote-notice-content"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
        />
      </section>
    </div>
  );
}

export function useHomeCommunications() {
  const carousel = useQuery({
    queryKey: ["home-carousel"],
    queryFn: engagementApi.carousel,
  });
  const ticker = useQuery({
    queryKey: ["notice", 3],
    queryFn: () => engagementApi.notice(3),
  });
  const announcement = useQuery({
    queryKey: ["notice", 2],
    queryFn: () => engagementApi.notice(2),
  });

  return {
    carousel: carousel.data || [],
    ticker: ticker.data?.content || "",
    announcement: announcement.data?.content || "",
  };
}
